import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';

const useStyles = makeStyles((theme) => ({
  panel: {
    boxSizing: 'border-box',
    padding: theme.spacing(2),
    width: '100%',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  headerIconButton: {
    borderRadius: '50%',
    height: 36,
    width: 36,
  },
  headerSpacer: {
    flex: 1,
  },
  primaryActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
    '& > button': {
      minHeight: 48,
    },
    [theme.breakpoints.down('xs')]: {
      '& > button': {
        width: '100%',
      },
    },
  },
  progress: {
    marginTop: theme.spacing(1),
  },
  title: {
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

const preparingStatuses = new Set(['queued', 'starting']);

const getTitle = (blocked, status) => {
  if (blocked) {
    return 'Your browser blocked the new tab.';
  }
  return preparingStatuses.has(status)
    ? 'Preparing your view'
    : 'Updating statement';
};

const PalRefreshPanel = ({
  blocked,
  canChangeProduct,
  onBack,
  onCancel,
  onOpen,
  refresh,
}) => {
  const classes = useStyles();
  const regionRef = useRef(null);
  const openRef = useRef(null);
  const title = getTitle(blocked, refresh?.response?.status ?? 'starting');

  useEffect(() => {
    (blocked ? openRef.current : regionRef.current)?.focus();
  }, [blocked]);

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onCancel();
    }
  };

  return (
    <Box
      aria-busy={!blocked}
      aria-label="PAL View As refresh"
      className={classes.panel}
      onKeyDown={handleKeyDown}
      ref={regionRef}
      role="region"
      tabIndex={-1}
    >
      <Box className={classes.header}>
        {canChangeProduct ? (
          <IconButton
            aria-label="Back"
            className={classes.headerIconButton}
            onClick={onBack}
            size="small"
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        ) : (
          <Box className={classes.headerSpacer} />
        )}
        <IconButton
          aria-label="Close"
          className={classes.headerIconButton}
          onClick={onCancel}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box aria-live="polite" role="status">
        <Typography className={classes.title} component="h2" variant="h6">
          {title}
        </Typography>
      </Box>
      {blocked ? (
        <Box className={classes.primaryActions}>
          <Button
            color="primary"
            disableFocusRipple
            onClick={onOpen}
            ref={openRef}
            variant="contained"
          >
            Open view
          </Button>
        </Box>
      ) : (
        <LinearProgress aria-label={title} className={classes.progress} />
      )}
    </Box>
  );
};

PalRefreshPanel.propTypes = {
  blocked: PropTypes.bool,
  canChangeProduct: PropTypes.bool,
  onBack: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onOpen: PropTypes.func,
  refresh: PropTypes.object,
};

PalRefreshPanel.defaultProps = {
  blocked: false,
  canChangeProduct: false,
  onOpen: undefined,
  refresh: null,
};

export default PalRefreshPanel;
